// LiveSignalStreamPanel — lifted verbatim out of LiveTrading.tsx (God 2026-08-26).
// Same code, smaller address: a UI problem in this panel is now a 136-line file
// to read instead of a 6,900-line one. Imports are derived from what the block
// actually references, not guessed.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Maximize2, Minimize2, Minus, Send, X } from 'lucide-react';
import { LiveSignal } from './foundation';
import { RudraFeedTab } from './RudraFeedTab';

export const LiveSignalStreamPanel: React.FC<{
  signals: LiveSignal[];
  onClose: () => void;
  minimized: boolean;
  onToggleMinimize: () => void;
  onDeploySignal?: (card: any) => void;
  onOpenChart?: (sym: string, model?: string) => void;
}> = ({ signals, onClose, minimized, onToggleMinimize, onDeploySignal, onOpenChart }) => {
  const [feedTab, setFeedTab] = useState<'TV' | 'SCALPING' | 'CASH' | 'FUTURES' | 'OPTION'>('TV');
  const [fullscreen, setFullscreen] = useState(false);
  // 🆕 v7.0 — minimized floating pill widget
  if (minimized) {
    const latestSignal = signals[0];
    const liveCount = signals.filter(s => s.status === 'PENDING' || s.status === 'EXECUTED').length;
    return (
      <div className="jc-float" onClick={onToggleMinimize} style={{
        position:'fixed', bottom:80, right:20, zIndex:9998,
        background:'linear-gradient(135deg,#a855f7,#7c3aed)',
        borderRadius:30, padding:'10px 16px', cursor:'pointer',
        boxShadow:'0 8px 32px rgba(168,85,247,.4)',
        display:'flex', alignItems:'center', gap:10, transition:'all .2s',
        border:'2px solid rgba(255,255,255,.15)'
      }}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px) scale(1.03)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0) scale(1)';}}
      title="Click to expand signal stream"
      >
        <Send size={16} color="#fff"/>
        <div style={{display:'flex',flexDirection:'column'}}>
          <div style={{fontSize:10,fontWeight:900,color:'#fff',letterSpacing:1}}>
            SIGNALS {signals.length > 0 && <span style={{background:'rgba(0,0,0,.3)',padding:'1px 6px',borderRadius:8,marginLeft:4,fontSize:9}}>{signals.length}</span>}
          </div>
          {latestSignal && (
            <div style={{fontSize:8,color:'rgba(255,255,255,.85)',marginTop:1}}>
              Latest: {latestSignal.action.toUpperCase()} {latestSignal.opt_type || ''} · {latestSignal.time}
            </div>
          )}
          {!latestSignal && <div style={{fontSize:8,color:'rgba(255,255,255,.7)',marginTop:1}}>Waiting...</div>}
        </div>
        <span className="pulse-dot" style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#10b981',marginLeft:4}}></span>
      </div>
    );
  }

  return (
    <div style={fullscreen
      ? {position:'fixed',top:8,left:8,right:8,bottom:8,width:'auto',background:'linear-gradient(180deg,#0a0f1e,#0d1117)',borderRadius:12,border:'1px solid #a855f7',boxShadow:'0 12px 48px rgba(168,85,247,.3)',display:'flex',flexDirection:'column',zIndex:9998,overflow:'hidden'}
      : {position:'fixed',top:80,right:20,bottom:20,width:'min(380px, calc(100vw - 40px))',background:'linear-gradient(180deg,#0a0f1e,#0d1117)',borderRadius:12,border:'1px solid #a855f7',boxShadow:'0 12px 48px rgba(168,85,247,.25)',display:'flex',flexDirection:'column',zIndex:9998,overflow:'hidden'}}>
      <div style={{padding:'12px 16px',background:'linear-gradient(90deg,rgba(168,85,247,.15),rgba(168,85,247,.05))',borderBottom:'1px solid #1e293b',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontSize:12,fontWeight:900,color:'#a855f7',letterSpacing:1,display:'flex',alignItems:'center',gap:6}}>
            <Send size={13}/> LIVE SIGNAL STREAM
            <span className="pulse-dot" style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#a855f7'}}></span>
          </div>
          <div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{feedTab === 'TV' ? `Last ${signals.length} TradingView signals` : `RUDRA ${feedTab} signals · 1-click deploy / chart`}</div>
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          {/* Full-screen toggle */}
          <button onClick={()=>setFullscreen(v=>!v)} title={fullscreen ? 'Exit full screen' : 'Full screen'}
            style={{background:'#1e293b',border:'1px solid #334155',borderRadius:6,width:24,height:24,cursor:'pointer',color:'#22d3ee',display:'flex',alignItems:'center',justifyContent:'center'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#22d3ee33';}}
            onMouseLeave={e=>{e.currentTarget.style.background='#1e293b';}}
          >
            {fullscreen ? <Minimize2 size={12}/> : <Maximize2 size={12}/>}
          </button>
          {/* 🆕 v7.0 — Minimize Button */}
          <button onClick={onToggleMinimize} title="Minimize"
            style={{background:'#1e293b',border:'1px solid #334155',borderRadius:6,width:24,height:24,cursor:'pointer',color:'#94a3b8',display:'flex',alignItems:'center',justifyContent:'center'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#a855f733';e.currentTarget.style.color='#a855f7';}}
            onMouseLeave={e=>{e.currentTarget.style.background='#1e293b';e.currentTarget.style.color='#94a3b8';}}
          >
            <Minus size={12}/>
          </button>
          <button onClick={onClose} title="Close"
            style={{background:'#1e293b',border:'1px solid #334155',borderRadius:6,width:24,height:24,cursor:'pointer',color:'#94a3b8',display:'flex',alignItems:'center',justifyContent:'center'}}
            onMouseEnter={e=>{e.currentTarget.style.background='#ef444433';e.currentTarget.style.color='#ef4444';}}
            onMouseLeave={e=>{e.currentTarget.style.background='#1e293b';e.currentTarget.style.color='#94a3b8';}}
          >
            <X size={12}/>
          </button>
        </div>
      </div>
      {/* RUDRA format tabs */}
      <div style={{display:'flex',gap:4,padding:'8px 10px',borderBottom:'1px solid #1e293b',overflowX:'auto'}}>
        {([['TV','📡 TV'],['SCALPING','⚡ Scalp'],['FUTURES','🔱 Futures'],['CASH','🏛️ Cash'],['OPTION','🎯 Option']] as const).map(([k,label])=>(
          <button key={k} onClick={()=>setFeedTab(k as any)} style={{padding:'4px 9px',borderRadius:6,fontSize:9,fontWeight:800,letterSpacing:.3,whiteSpace:'nowrap',cursor:'pointer',
            background: feedTab===k ? 'linear-gradient(135deg,#a855f7,#7c3aed)' : '#0d1117',
            color: feedTab===k ? '#fff' : '#94a3b8', border:`1px solid ${feedTab===k?'#a855f7':'#334155'}`}}>{label}</button>
        ))}
      </div>
      <div className="lt-scroll" style={{flex:1,overflowY:'auto',padding:8}}>
        {feedTab !== 'TV' ? (
          <RudraFeedTab tab={feedTab} wide={fullscreen} onDeploy={(c)=>onDeploySignal?.(c)} onChart={(s)=>onOpenChart?.(s,'VEG')} />
        ) : (<>
        {signals.length === 0 && (
          <div style={{textAlign:'center',padding:40,color:'#475569',fontSize:11}}>
            📡 Waiting for signals…<br/>
            <span style={{fontSize:9,color:'#64748b',marginTop:8,display:'block'}}>Send TradingView alert to webhook</span>
          </div>
        )}
        {signals.map(s => {
          const col = s.action.toUpperCase() === 'BUY' ? '#10b981' : '#ef4444';
          const stCol = s.status === 'EXECUTED' ? '#10b981' : s.status === 'FAILED' ? '#ef4444' : '#fbbf24';
          return (
            <div key={s.id} style={{padding:10,marginBottom:6,background:'#0d1117',borderRadius:8,border:`1px solid ${col}33`,borderLeft:`3px solid ${col}`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:10,fontWeight:900,color:col,letterSpacing:1}}>{s.action.toUpperCase()}</span>
                  {s.opt_type && <span style={{fontSize:8,fontWeight:900,padding:'1px 4px',borderRadius:3,background:s.opt_type==='CE'?'#10b981':'#ef4444',color:'#fff'}}>{s.opt_type}</span>}
                  <span style={{fontSize:9,color:'#94a3b8'}}>{s.symbol}</span>
                  {s.strike && <span style={{fontSize:9,fontWeight:800,color:'#fbbf24'}}>{s.strike}</span>}
                </div>
                <span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:`${stCol}15`,color:stCol,fontWeight:800,letterSpacing:1}}>{s.status}</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:9,color:'#64748b'}}>
                <div>📡 Spot: <span style={{color:'#fff',fontWeight:700,fontFamily:'monospace'}}>₹{s.spot?.toFixed(2)}</span></div>
                <div style={{textAlign:'right'}}>💰 LTP: <span style={{color:'#fbbf24',fontWeight:700,fontFamily:'monospace'}}>₹{s.ltp?.toFixed(2)}</span></div>
              </div>
              {s.comment && <div style={{fontSize:9,color:'#a855f7',marginTop:4,fontStyle:'italic'}}>"{s.comment}"</div>}
              <div style={{fontSize:8,color:'#475569',marginTop:4,display:'flex',justifyContent:'space-between'}}>
                <span>{s.bot_name}</span>
                <span>{s.time}</span>
              </div>
            </div>
          );
        })}
        </>)}
      </div>
    </div>
  );
};
// ══════════════════════════════════════════════════════════════
//  ⭐ v11.0 — MARKET DEPTH PANEL (20-Level Dhan HQ Order Book)
//  WebSocket: wss://api.jaiswalcapital.com/ws/market-depth
//  Shows Top-5 Bids (green) + Top-5 Asks (red) with depth bars
// ══════════════════════════════════════════════════════════════
